import { CalendarX, ClipboardList, FileClock, FileQuestion, MoonStar, Package, Radar, RefreshCw } from '@orgus/ui/icons';
import { useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Button, cn, EmptyState, ErrorState, formatDuration, GroupChevron, IconButton, Panel, plural, Pressable, RelativeTime, Select, Switch, toast, toUiError, Tooltip } from '@orgus/ui';
import { DISCIPLINE_DRIFT_INFO, DISCIPLINE_DRIFT_KINDS } from '@orgus/protocol';
import type { DisciplineDriftItem, DisciplineDriftKind, DisciplineDriftReport, DisciplineDriftSchedule } from '@orgus/protocol';
import { useDisciplineActions, useMissionsPresent } from './actions.ts';

const ICON: Record<DisciplineDriftKind, ComponentType<{ className?: string }>> = {
  'stale-doc': FileClock,
  'orphan-file': FileQuestion,
  'unused-dep': Package,
  'expired-waiver': CalendarX,
};

export function driftTask(projectId: string, projectName: string, kind: DisciplineDriftKind, items: readonly DisciplineDriftItem[]) {
  const info = DISCIPLINE_DRIFT_INFO[kind];
  return {
    projectId,
    kind: 'chore' as const,
    priority: 2 as const,
    title: `${info.title}: ${plural(items.length, 'item')} in ${projectName}`.slice(0, 200),
    body: [`Found by the nightly drift scan.`, '', `How to fix: ${info.fix}`, '', ...items.slice(0, 40).map((i) => `- ${i.path}: ${i.message}`), ...(items.length > 40 ? [`- and ${items.length - 40} more`] : []), '', `\`orgus-discipline:${projectId}:drift/${kind}:*\``].join('\n'),
  };
}

/** One kind of finding in a repo: a header row that folds (count and Open task on the same line in every group), the items and the fix inside. */
function Kind({ d, kind, defaultOpen }: { d: DisciplineDriftReport; kind: DisciplineDriftKind; defaultOpen: boolean }) {
  const actions = useDisciplineActions();
  const missions = useMissionsPresent();
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(defaultOpen);
  // What failed is said in the card, never in a toast.
  const [error, setError] = useState<unknown>(null);
  const items = d.items.filter((i) => i.kind === kind);
  const count = d.counts[kind];
  if (!count) return null;
  const Icon = ICON[kind];
  const info = DISCIPLINE_DRIFT_INFO[kind];
  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      await actions.openTask(driftTask(d.projectId, d.projectName ?? d.projectId, kind, items));
      toast.success('Task opened', `${info.title} in ${d.projectName ?? d.projectId} is in the backlog.`);
    } catch (e) {
      setError(toUiError(e, { what: 'The task was not opened.', next: 'Try again in a moment.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="overflow-hidden rounded-md border border-border-subtle bg-bg">
      <div className={cn('flex min-w-0 items-center gap-2 pr-2', expanded && 'border-b border-border-subtle')}>
        <Pressable onClick={() => setExpanded((x) => !x)} aria-expanded={expanded} className="flex h-row min-w-0 flex-1 items-center gap-2 pl-3 text-left text-sm font-medium text-fg hover:bg-hover">
          <GroupChevron open={expanded} />
          <Icon className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
          <span className="truncate">{info.title}</span>
          <span className="shrink-0 font-normal text-fg-subtle num">{count}</span>
        </Pressable>
        <Tooltip content={missions ? undefined : 'The missions module is not on this hub, so there is no backlog to add to.'}>
          <span className="shrink-0">
            <Button size="sm" variant="ghost" icon={<ClipboardList />} onClick={() => void open()} loading={busy} disabled={!missions} className="hidden sm:inline-flex">
              Open task
            </Button>
            <IconButton label={`Open a task for ${info.title.toLowerCase()}`} icon={<ClipboardList />} onClick={() => void open()} loading={busy} disabled={!missions} noTooltip={!missions} className="sm:hidden" />
          </span>
        </Tooltip>
      </div>
      {expanded && (
        <>
          <ul>
            {items.slice(0, 8).map((i, n) => (
              <li key={`${i.path}:${n}`} className="flex min-w-0 flex-col gap-0.5 border-b border-border-subtle px-3 py-1.5 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-3">
                <span className="min-w-0 shrink-0 truncate font-mono text-xs text-fg sm:max-w-1/2">{i.path}</span>
                <span className="min-w-0 truncate text-sm text-fg-muted">{i.message}</span>
              </li>
            ))}
            {count > Math.min(8, items.length) && <li className="px-3 py-1.5 text-sm text-fg-subtle">and {count - Math.min(8, items.length)} more</li>}
          </ul>
          <p className="border-t border-border-subtle px-3 py-1.5 text-xs text-fg-subtle">Fix: {info.fix}</p>
        </>
      )}
      {error != null && <ErrorState variant="inline" error={error} className="m-3" />}
    </div>
  );
}

/** A repo's drift: one folding panel with its count; the first repo and its first group open. */
function RepoDrift({ d, pending, defaultOpen }: { d: DisciplineDriftReport; pending: boolean; defaultOpen: boolean }) {
  const actions = useDisciplineActions();
  const total = Object.values(d.counts).reduce((a, b) => a + b, 0);
  const [runError, setRunError] = useState<unknown>(null);
  const name = d.projectName ?? d.projectId;
  const run = async () => {
    setRunError(null);
    try {
      await actions.driftNow(d.projectId);
      toast('Drift scan started', { description: `${name} is scanned now; results show here when the machine reports.` });
    } catch (e) {
      setRunError(toUiError(e, { what: 'The drift scan did not start.', next: 'Make sure the machine holding the repo is online.' }));
    }
  };
  const spin = <RefreshCw className={cn(pending && 'animate-spin')} />;
  return (
    <Panel
      title={name}
      sub={total ? plural(total, 'item') : 'Nothing drifted'}
      icon={<Radar />}
      collapsible
      defaultOpen={defaultOpen}
      actions={
        <>
          <Button size="sm" variant="secondary" icon={spin} onClick={() => void run()} disabled={pending} className="hidden sm:inline-flex">
            {pending ? 'Scanning' : 'Scan now'}
          </Button>
          <IconButton label={pending ? `Scanning ${name}` : `Scan ${name} now`} icon={spin} onClick={() => void run()} disabled={pending} className="sm:hidden" />
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <span className="inline-flex flex-wrap items-center gap-1 text-caption text-fg-subtle">
          {d.trigger === 'schedule' ? 'Nightly scan' : 'Asked for'} <RelativeTime ts={d.at} /> (took {formatDuration(d.durationMs)})
        </span>
        {runError != null && <ErrorState variant="inline" error={runError} />}
        {d.error && (
          <ErrorState
            variant="inline"
            error={
              d.partial
                ? { what: 'The scan was not complete.', why: d.error, next: 'Very large repos are read in part. Run orgus drift in the repo for a full scan.' }
                : { what: 'The drift scan failed.', why: d.error, next: 'Make sure the machine holding the repo is online and has the checkout, then scan again.' }
            }
          />
        )}
        {DISCIPLINE_DRIFT_KINDS.filter((k) => d.counts[k]).map((k, i) => (
          <Kind key={k} d={d} kind={k} defaultOpen={i === 0} />
        ))}
      </div>
    </Panel>
  );
}

const HOURS = Array.from({ length: 24 }, (_, h) => ({ value: String(h), label: `${String(h).padStart(2, '0')}:00` }));

/** The nightly drift scan: when it runs, what it found per repo, and tasks from it. */
export function DriftView({ drift, schedule, pending, repos }: { drift: DisciplineDriftReport[]; schedule: DisciplineDriftSchedule; pending: string[]; repos: number }) {
  const actions = useDisciplineActions();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const sorted = useMemo(() => [...drift].sort((a, b) => Object.values(b.counts).reduce((x, y) => x + y, 0) - Object.values(a.counts).reduce((x, y) => x + y, 0)), [drift]);
  const save = async (next: DisciplineDriftSchedule) => {
    setSaving(true);
    setError(null);
    try {
      await actions.setDriftSchedule(next);
      toast.success('Schedule saved', next.enabled ? `Each machine scans its repos at ${String(next.hour).padStart(2, '0')}:00 local time.` : 'The nightly drift scan is off.');
    } catch (e) {
      setError(toUiError(e, { what: 'The schedule was not saved.', next: 'Only an owner or admin can change it.' }));
    } finally {
      setSaving(false);
    }
  };
  const runAll = async () => {
    setError(null);
    try {
      await actions.driftNow();
      toast('Drift scans started', { description: 'Each machine scans its repos one at a time.' });
    } catch (e) {
      setError(toUiError(e, { what: 'The drift scans did not start.', next: 'Start an agent, then try again.' }));
    }
  };
  const busy = !repos || pending.length > 0;
  return (
    <div className="flex flex-col gap-4">
      <Panel
        title="Nightly drift scan"
        sub={<span className="hidden sm:inline">Read-only</span>}
        icon={<MoonStar />}
        actions={
          <>
            <Button size="sm" variant="secondary" icon={<RefreshCw />} onClick={() => void runAll()} disabled={busy} className="hidden sm:inline-flex">
              Scan every repo now
            </Button>
            <IconButton label="Scan every repo now" icon={<RefreshCw />} onClick={() => void runAll()} disabled={busy} className="sm:hidden" />
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-ui text-fg">
            <Switch checked={schedule.enabled} onCheckedChange={(v) => void save({ ...schedule, enabled: v === true })} disabled={saving} />
            Scan every night at
          </label>
          <Select size="sm" aria-label="Hour" value={String(schedule.hour)} onValueChange={(v) => void save({ ...schedule, hour: Number(v) })} options={HOURS} disabled={!schedule.enabled || saving} />
          <span className="text-caption text-fg-subtle">Local time on each machine</span>
        </div>
        {error != null && <ErrorState variant="inline" error={error} className="mt-3" />}
      </Panel>
      {sorted.length ? (
        sorted.map((d, i) => <RepoDrift key={d.projectId} d={d} pending={pending.includes(d.projectId)} defaultOpen={i === 0} />)
      ) : (
        <div className="rounded-lg border border-border bg-surface">
          <EmptyState icon={<Radar />} title="No drift scan has run yet" description="Scan every repo now to see results today." />
        </div>
      )}
    </div>
  );
}
